import { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Camera, Upload, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const CustomerForm = ({ onSubmit, loading, initialData = null, isEditMode = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    reg_no: '',
    age: '',
    address: '',
    phone_number: '',
    email: '',
    problem: '',
    photo: null,
    sessions_planned: 3,
    notes: '',
    before_report: null,
    after_report: null
  });

  const [showWebcam, setShowWebcam] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [beforeReportPreview, setBeforeReportPreview] = useState(null);
  const [afterReportPreview, setAfterReportPreview] = useState(null);
  
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const beforeReportRef = useRef(null);
  const afterReportRef = useRef(null);

  // Initialize form with existing data in edit mode
  useEffect(() => {
    if (isEditMode && initialData) {
      setFormData({
        name: initialData.name || '',
        reg_no: initialData.reg_no || '',
        age: initialData.age || '',
        address: initialData.address || '',
        phone_number: initialData.phone_number || '',
        email: initialData.email || '',
        problem: initialData.problem || '',
        photo: initialData.photo || null,
        sessions_planned: initialData.sessions_planned || 3,
        notes: initialData.notes || '',
        before_report: initialData.before_report || null,
        after_report: initialData.after_report || null
      });
      if (initialData.photo) {
        setPhotoPreview(initialData.photo);
      }
      if (initialData.before_report) {
        setBeforeReportPreview('before_report.pdf');
      }
      if (initialData.after_report) {
        setAfterReportPreview('after_report.pdf');
      }
    }
  }, [isEditMode, initialData]);

  const startWebcam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: { ideal: 'environment' }, // Use back camera on mobile/tablet
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      streamRef.current = stream;
      setShowWebcam(true);
      
      // Wait for next tick to ensure video element is rendered
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(err => {
            console.error('Video play error:', err);
          });
        }
      }, 100);
    } catch (error) {
      toast.error('Unable to access webcam');
      console.error('Webcam error:', error);
    }
  };

  const stopWebcam = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowWebcam(false);
  };

  const capturePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);
      const photoData = canvas.toDataURL('image/jpeg', 0.8);
      setFormData({ ...formData, photo: photoData });
      setPhotoPreview(photoData);
      stopWebcam();
      toast.success('Photo captured successfully');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, photo: reader.result });
        setPhotoPreview(reader.result);
        toast.success('Photo uploaded successfully');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleReportUpload = (type, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'before') {
          setFormData({ ...formData, before_report: reader.result });
          setBeforeReportPreview(file.name);
        } else {
          setFormData({ ...formData, after_report: reader.result });
          setAfterReportPreview(file.name);
        }
        toast.success(`${type === 'before' ? 'Before' : 'After'} report uploaded`);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Photo Section */}
      <div className="border-b pb-6">
        <Label className="text-xs uppercase tracking-[0.2em] font-semibold block mb-3">Customer Photo</Label>
        
        {photoPreview ? (
          <div className="relative inline-block">
            <img src={photoPreview} alt="Customer" className="w-32 h-32 rounded-lg object-cover border-2 border-[#E5E5DF]" />
            <button
              type="button"
              onClick={() => {
                setPhotoPreview(null);
                setFormData({ ...formData, photo: '' });
              }}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
            >
              <X size={16} />
            </button>
          </div>
        ) : showWebcam ? (
          <div className="space-y-3">
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline 
              muted
              className="w-full max-w-md rounded-lg border-2 border-[#2C4C3B] bg-black"
            />
            <div className="flex gap-2">
              <Button type="button" onClick={capturePhoto} className="bg-[#2C4C3B] hover:bg-[#2C4C3B]/90">
                <Camera size={20} className="mr-2" />
                Capture
              </Button>
              <Button type="button" onClick={stopWebcam} variant="outline">
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex gap-3">
            <Button type="button" onClick={startWebcam} className="bg-[#2C4C3B] hover:bg-[#2C4C3B]/90">
              <Camera size={20} className="mr-2" />
              Open Webcam
            </Button>
            <Button type="button" onClick={() => fileInputRef.current?.click()} variant="outline">
              <Upload size={20} className="mr-2" />
              Upload Photo
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>
        )}
      </div>

      {/* Basic Info */}
      {!isEditMode && (
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Registration Number</Label>
          <Input
            data-testid="customer-regno-input"
            value={formData.reg_no}
            onChange={(e) => setFormData({ ...formData, reg_no: e.target.value.toUpperCase() })}
            placeholder="Auto-assigned on save"
            className="font-mono"
          />
          <p className="text-xs text-[#1C1C1A]/60 mt-1">Leave blank to auto-assign the next number for this branch at save time</p>
        </div>
      )}

      {isEditMode && (
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Registration Number</Label>
          <Input
            value={formData.reg_no}
            disabled
            className="font-mono bg-[#E2E8E4] cursor-not-allowed"
          />
          <p className="text-xs text-[#1C1C1A]/60 mt-1">Registration number cannot be changed</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Full Name *</Label>
          <Input
            data-testid="customer-name-input"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Age</Label>
          <Input
            data-testid="customer-age-input"
            type="number"
            min="1"
            max="150"
            value={formData.age}
            onChange={(e) => setFormData({ ...formData, age: e.target.value })}
          />
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Address</Label>
        <Textarea
          data-testid="customer-address-input"
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          rows={2}
          className="resize-none"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Phone Number *</Label>
          <Input
            data-testid="customer-phone-input"
            type="tel"
            value={formData.phone_number}
            onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
            required
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Email</Label>
          <Input
            data-testid="customer-email-input"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Health Problem / Reason for Therapy</Label>
        <Textarea
          data-testid="customer-problem-input"
          value={formData.problem}
          onChange={(e) => setFormData({ ...formData, problem: e.target.value })}
          rows={3}
          placeholder="Describe the health issue or reason for seeking therapy..."
          className="resize-none"
        />
      </div>

      <div>
        <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Planned Sessions</Label>
        <Input
          data-testid="customer-sessions-input"
          type="number"
          min="1"
          value={formData.sessions_planned}
          onChange={(e) => setFormData({ ...formData, sessions_planned: parseInt(e.target.value) })}
        />
      </div>

      {/* Reports Section */}
      <div className="border-t pt-6">
        <Label className="text-xs uppercase tracking-[0.2em] font-semibold block mb-3">Medical Reports</Label>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm text-[#1C1C1A]/70 mb-2 block">Before Report</Label>
            {beforeReportPreview ? (
              <div className="flex items-center justify-between p-3 bg-[#E2E8E4] rounded-lg">
                <span className="text-sm truncate">{beforeReportPreview}</span>
                <button
                  type="button"
                  onClick={() => {
                    setBeforeReportPreview(null);
                    setFormData({ ...formData, before_report: null });
                  }}
                  className="text-red-500 hover:text-red-600"
                >
                  <X size={18} />
                </button>
              </div>
            ) : (
              <Button
                type="button"
                onClick={() => beforeReportRef.current?.click()}
                variant="outline"
                className="w-full"
              >
                <Upload size={18} className="mr-2" />
                Upload Before Report
              </Button>
            )}
            <input
              ref={beforeReportRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={(e) => handleReportUpload('before', e)}
              className="hidden"
            />
          </div>

          <div>
            <Label className="text-sm text-[#1C1C1A]/70 mb-2 block">After Report</Label>
            {afterReportPreview ? (
              <div className="flex items-center justify-between p-3 bg-[#E2E8E4] rounded-lg">
                <span className="text-sm truncate">{afterReportPreview}</span>
                <button
                  type="button"
                  onClick={() => {
                    setAfterReportPreview(null);
                    setFormData({ ...formData, after_report: null });
                  }}
                  className="text-red-500 hover:text-red-600"
                >
                  <X size={18} />
                </button>
              </div>
            ) : (
              <Button
                type="button"
                onClick={() => afterReportRef.current?.click()}
                variant="outline"
                className="w-full"
              >
                <Upload size={18} className="mr-2" />
                Upload After Report
              </Button>
            )}
            <input
              ref={afterReportRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={(e) => handleReportUpload('after', e)}
              className="hidden"
            />
          </div>
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Additional Notes</Label>
        <Textarea
          data-testid="customer-notes-input"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows={2}
          className="resize-none"
        />
      </div>

      <Button
        data-testid="submit-customer-button"
        type="submit"
        disabled={loading}
        className="w-full bg-[#2C4C3B] hover:bg-[#2C4C3B]/90 text-white rounded-md py-3"
      >
        {loading ? (isEditMode ? 'Updating...' : 'Adding Customer...') : (isEditMode ? 'Update Customer' : 'Add Customer')}
      </Button>
    </form>
  );
};

export default CustomerForm;
